/* Offline product catalog cache.
 *
 * The product picker searches on the SERVER (products/?search=…), so with no
 * internet a search returns nothing. This keeps a full copy of the catalog on
 * the phone (refreshed while online) and searches it locally when the server
 * can't be reached — so offline search works just like online.
 *
 * Storage: IndexedDB (not localStorage) because the whole catalog can be a few
 * MB — well past the localStorage budget the sale queue shares. One record holds
 * the full array + a timestamp. An in-memory copy makes repeated searches fast.
 *
 * Customers and services already work offline: the sale screen loads them in
 * full (getAllCustomers / getAllServices) and filters them in the browser. Only
 * products used server-side search, which is what this closes.
 */
import { apiClient } from '../services/authService';
import { filterSearch } from '../utils/search';

const DB_NAME = 'kistore-offline';
const STORE = 'kv';
const KEY = 'products';
const REFRESH_THROTTLE_MS = 5 * 60 * 1000;

let inMemory = null;
let lastRefresh = 0;

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbSet(key, val) {
  const db = await openDB();
  return new Promise((res, rej) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(val, key);
    tx.oncomplete = () => res();
    tx.onerror = () => rej(tx.error);
  });
}

async function idbGet(key) {
  const db = await openDB();
  return new Promise((res, rej) => {
    const tx = db.transaction(STORE, 'readonly');
    const r = tx.objectStore(STORE).get(key);
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
  });
}

/**
 * Pure catalog search — mirrors what the server does for the sale picker
 * (match by name, plus barcode/reference when present) and paginates the same
 * { results, count } shape. Exported so it can be unit-tested without a browser.
 */
export function filterProducts(all, { search = '', page = 1, pageSize = 30 } = {}) {
  // Règle Ki Para (utils/search.js) — la même que le serveur : début de mot
  // sur le nom, codes « contient », « commence par » en tête.
  const matched = filterSearch(all, search, {
    text: (p) => p.name, codes: (p) => [p.barcode, p.reference, p.code],
  });
  const start = (Math.max(1, page) - 1) * pageSize;
  return { results: matched.slice(start, start + pageSize), count: matched.length, offline: true };
}

/** Pull the whole catalog from the server and store it locally (throttled). */
export async function refreshProductCache(force = false) {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return;
  const now = Date.now();
  // Perf (2026-10-05) : la date du dernier rafraîchissement survit au
  // rechargement de la page (elle est stockée avec la copie), sinon CHAQUE
  // ouverture de l'app retéléchargeait tout le catalogue (≈ 700 Ko, 0,6 s
  // serveur) alors que la copie locale datait de quelques secondes.
  if (!force && !lastRefresh) {
    try {
      const rec = await idbGet(KEY);
      if (rec?.updatedAt) lastRefresh = rec.updatedAt;
    } catch { /* IDB unavailable */ }
  }
  if (!force && now - lastRefresh < REFRESH_THROTTLE_MS) return;
  lastRefresh = now;
  try {
    const res = await apiClient.get('products/?page_size=5000');
    const data = res.data;
    const list = Array.isArray(data) ? data : (data?.results || []);
    if (list.length) {
      inMemory = list;
      try { await idbSet(KEY, { products: list, updatedAt: now }); } catch { /* IDB unavailable */ }
    }
  } catch {
    // Offline or transient — keep whatever we cached before.
    lastRefresh = 0; // allow an immediate retry next time we're online
  }
}

let lastStored = 0;
const STORE_THROTTLE_MS = 60 * 1000;

/** The caisse already downloaded the full catalog: keep it as the offline copy
 *  (no second download; the device write is throttled). */
export function rememberCatalog(list) {
  if (!Array.isArray(list) || !list.length) return;
  inMemory = list;
  const now = Date.now();
  lastRefresh = now;
  if (now - lastStored < STORE_THROTTLE_MS) return;
  lastStored = now;
  idbSet(KEY, { products: list, updatedAt: now }).catch(() => { /* IDB unavailable */ });
}

/** Last known catalog (memory, then this device) — paints the caisse at once. */
export async function lastKnownCatalog() {
  return getCachedProducts();
}

async function getCachedProducts() {
  if (inMemory) return inMemory;
  try {
    const rec = await idbGet(KEY);
    inMemory = rec?.products || [];
  } catch {
    inMemory = [];
  }
  return inMemory;
}

/** Offline product search against the locally cached catalog. */
export async function searchLocalProducts(opts = {}) {
  const all = await getCachedProducts();
  return filterProducts(all, opts);
}
